import React, { useContext, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { PropTypes } from 'prop-types';
import { compose } from 'recompose';
import { CircularProgress, TextField } from '@mui/material';

import withPortal from '../withPortal';
import ChatMessage from '../../Model/ChatMessage';
import ChatContext from '../../context/ChatContext';
import { AssignmentRole } from '../../../utils/internalAssignments';
import useAssigneeOptions from '../../hooks/useAssigneeOptions';
import useToast from '../../hooks/useToast';
import {
  Container,
  StyledModal,
  Title,
  Subtitle,
  AssigneeList,
  AssigneeItem,
  AssigneeName,
  AssigneeRole,
  LoadingSpinnerContainer,
  ControlsContainer,
  StyledButtonContainer,
  StyledSecondaryButton,
  StyledPrimaryButton,
} from './styles';
import texts from './texts.json';

const FlagAssigneeModal = ({ isOpen, onClose, message }) => {
  const { authUser } = useContext(ChatContext);
  const { assigneeOptions, isLoading } = useAssigneeOptions();
  const { showToast } = useToast();
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!selectedAssigneeId) return;
    const selectedAssignee = assigneeOptions.find((a) => a.id === selectedAssigneeId);
    setIsSubmitting(true);
    try {
      await message.flag(authUser.id, selectedAssigneeId, selectedAssignee?.name, flagReason);
      onClose();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          messageId: message.id,
          selectedAssigneeId,
          coachId: authUser?.id,
        },
      });
      showToast(texts.error, { error: true });
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Container>
      <StyledModal>
        <Title>{texts.title}</Title>
        <Subtitle>{texts.subtitle}</Subtitle>
        {isLoading ? (
          <LoadingSpinnerContainer>
            <CircularProgress size={28} />
          </LoadingSpinnerContainer>
        ) : (
          <AssigneeList>
            {assigneeOptions.map((assignee) => (
              <AssigneeItem
                key={assignee.id}
                $selected={selectedAssigneeId === assignee.id}
                onClick={() => setSelectedAssigneeId(assignee.id)}
                type="button"
              >
                <AssigneeName $selected={selectedAssigneeId === assignee.id}>
                  {assignee.name}
                </AssigneeName>
                <AssigneeRole>
                  {assignee.role === AssignmentRole.ASSISTANT_COACH ? texts.roleAC : texts.roleCoach}
                </AssigneeRole>
              </AssigneeItem>
            ))}
          </AssigneeList>
        )}
        <TextField
          label={texts.reasonLabel}
          placeholder={texts.reasonPlaceholder}
          value={flagReason}
          onChange={(e) => setFlagReason(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          size="small"
          disabled={isSubmitting}
          sx={{ mt: 2 }}
        />
        <ControlsContainer>
          <StyledButtonContainer>
            <StyledSecondaryButton
              onClick={onClose}
              full={false}
              disabled={isSubmitting}
            >
              {texts.cancel}
            </StyledSecondaryButton>
            <StyledPrimaryButton
              onClick={handleConfirm}
              full={false}
              disabled={!selectedAssigneeId || isSubmitting}
            >
              {texts.confirm}
            </StyledPrimaryButton>
          </StyledButtonContainer>
        </ControlsContainer>
      </StyledModal>
    </Container>
  );
};

FlagAssigneeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  message: PropTypes.instanceOf(ChatMessage).isRequired,
};

export default compose(
  withPortal('flagAssigneeModal'),
)(FlagAssigneeModal);
